import { html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

const VOLUME_OPTIONS: { label: string; value: number }[] = [
    { label: 'Off', value: 0 },
    { label: 'Low', value: 0.25 },
    { label: 'Med', value: 0.5 },
    { label: 'High', value: 1.0 },
];

/**
 * Metronome volume control with dropdown.
 *
 * @fires metronome-change - Fired when volume changes (detail: { volume: number })
 */
@customElement('metronome-control')
export class MetronomeControl extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: inline-block;
                position: relative;
            }

            .toggle-btn {
                display: flex;
                align-items: center;
                gap: 0.25rem;
                background: #333;
                border: none;
                border-radius: 1rem;
                padding: 0.25rem 0.5rem;
                color: white;
                font-size: 0.85rem;
                cursor: pointer;
                transition: background 0.2s;
            }

            .toggle-btn:hover:not([disabled]) {
                background: #444;
            }

            .toggle-btn[disabled] {
                opacity: 0.5;
                cursor: not-allowed;
            }

            .icon {
                font-size: 0.9rem;
            }

            .arrow {
                font-size: 0.6rem;
                margin-left: 0.125rem;
            }

            .dropdown {
                position: absolute;
                top: 100%;
                left: 50%;
                transform: translateX(-50%);
                background: #333;
                border-radius: 0.5rem;
                padding: 0.25rem 0;
                margin-top: 0.25rem;
                min-width: 80px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
                z-index: 100;
                display: none;
            }

            :host([open]) .dropdown {
                display: block;
            }

            .option {
                padding: 0.5rem 0.75rem;
                color: white;
                font-size: 0.85rem;
                cursor: pointer;
                text-align: center;
                transition: background 0.15s;
            }

            .option:hover {
                background: #444;
            }

            .option.selected {
                background: var(--primary-color, #2563eb);
            }
        `,
    ];

    @property({ type: Number })
    volume = 0;

    @property({ type: Boolean, reflect: true })
    disabled = false;

    @property({ type: Boolean, reflect: true })
    open = false;

    private _boundCloseOnOutsideClick = this._closeOnOutsideClick.bind(this);

    connectedCallback(): void {
        super.connectedCallback();
        document.addEventListener('click', this._boundCloseOnOutsideClick);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        document.removeEventListener('click', this._boundCloseOnOutsideClick);
    }

    private _closeOnOutsideClick(e: Event): void {
        if (!this.open) return;
        const path = e.composedPath();
        if (!path.includes(this)) {
            this.open = false;
        }
    }

    private _toggle(e: Event): void {
        if (this.disabled) return;
        e.stopPropagation();
        this.open = !this.open;
    }

    private _selectOption(value: number): void {
        this.volume = value;
        this.open = false;
        this.dispatchEvent(
            new CustomEvent('metronome-change', {
                bubbles: true,
                composed: true,
                detail: { volume: value },
            })
        );
    }

    private _getLabel(): string {
        if (this.volume >= 1) return 'High';
        if (this.volume >= 0.5) return 'Med';
        if (this.volume > 0) return 'Low';
        return 'Off';
    }

    render() {
        return html`
            <button
                class="toggle-btn"
                ?disabled=${this.disabled}
                @click=${this._toggle}
                aria-expanded=${this.open}
                aria-haspopup="listbox"
            >
                <span class="icon">${'\uD83D\uDD14'}</span>
                <span>${this._getLabel()}</span>
                <span class="arrow">${'\u25BC'}</span>
            </button>
            <div class="dropdown" role="listbox">
                ${VOLUME_OPTIONS.map(
                    opt => html`
                        <div
                            class="option ${this.volume === opt.value ? 'selected' : ''}"
                            role="option"
                            aria-selected=${this.volume === opt.value}
                            @click=${() => this._selectOption(opt.value)}
                        >${opt.label}</div>
                    `
                )}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'metronome-control': MetronomeControl;
    }
}
